import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, ArrowRight, ArrowLeft, Check, Upload, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EXAMS } from "@/data/mock";
import { track, EVENTS } from "@/lib/analytics";

const LEVELS = ["Just starting", "Beginner", "Intermediate", "Advanced"];
const HOURS = ["1-2 hrs", "3-4 hrs", "5-6 hrs", "7+ hrs"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ exam: "", target: "", exam_date: "", level: "", daily_hours: "", mock_score: "" });
  const [building, setBuilding] = useState(false);
  const [done, setDone] = useState(false);

  const total = 7;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, total - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canProceed = () => {
    if (step === 0) return !!data.exam;
    if (step === 1) return !!data.target;
    if (step === 2) return !!data.exam_date;
    if (step === 3) return !!data.level;
    if (step === 4) return !!data.daily_hours;
    return true;
  };

  const finish = async () => {
    setBuilding(true);
    track(EVENTS.FIRST_MOCK_UPLOADED, { exam: data.exam });
    try {
      await api.post("/onboarding", data);
      track(EVENTS.FIRST_DIAGNOSIS, { exam: data.exam });
      track(EVENTS.ONBOARDING_COMPLETED, { exam: data.exam });
      await new Promise((r) => setTimeout(r, 2200));
      await refreshUser();
      setDone(true);
      setTimeout(() => navigate("/dashboard"), 2600);
    } catch (e) {
      setBuilding(false);
    }
  };

  const steps = [
    {
      title: "Which exam are you preparing for?",
      sub: "We'll tailor everything to this exam.",
      body: (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {EXAMS.map((ex) => (
            <button
              key={ex.id}
              disabled={ex.status !== "live"}
              onClick={() => set("exam", ex.name)}
              className={`relative rounded-xl border px-4 py-4 text-sm font-medium transition-colors ${
                data.exam === ex.name ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"
              } ${ex.status !== "live" ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`onboarding-exam-${ex.id}`}
            >
              {ex.name}
              {ex.status !== "live" && <span className="block text-[10px] text-muted-foreground mt-1">Coming soon</span>}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "What's your target score or rank?",
      sub: "Tell us what a great result looks like for you.",
      body: (
        <input
          type="text" value={data.target} onChange={(e) => set("target", e.target.value)}
          className="w-full rounded-xl border border-input bg-background px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          placeholder="e.g. 99 percentile / AIR under 1000 / 180+ marks" data-testid="onboarding-target"
        />
      ),
    },
    {
      title: "When is your exam?",
      sub: "This shapes how aggressive your plan should be.",
      body: (
        <input
          type="date" value={data.exam_date} onChange={(e) => set("exam_date", e.target.value)}
          className="w-full rounded-xl border border-input bg-background px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          data-testid="onboarding-date"
        />
      ),
    },
    {
      title: "What's your current preparation level?",
      sub: "Be honest — this only helps the plan.",
      body: (
        <div className="grid grid-cols-2 gap-3">
          {LEVELS.map((l) => (
            <button key={l} onClick={() => set("level", l)}
              className={`rounded-xl border px-4 py-4 text-sm font-medium transition-colors ${
                data.level === l ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"
              }`} data-testid={`onboarding-level-${l}`}>
              {l}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "How many hours can you study daily?",
      sub: "Your plan fits the time you actually have.",
      body: (
        <div className="grid grid-cols-2 gap-3">
          {HOURS.map((h) => (
            <button key={h} onClick={() => set("daily_hours", h)}
              className={`rounded-xl border px-4 py-4 text-sm font-medium transition-colors ${
                data.daily_hours === h ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"
              }`} data-testid={`onboarding-hours-${h}`}>
              {h}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "Upload your latest mock (optional)",
      sub: "Add your latest score so the AI can calibrate — you can skip and add later.",
      body: (
        <div>
          <div className="rounded-xl border-2 border-dashed border-border px-4 py-8 text-center">
            <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Drag a mock report here, or just enter your score below.</p>
          </div>
          <input
            type="text" value={data.mock_score} onChange={(e) => set("mock_score", e.target.value)}
            className="mt-4 w-full rounded-xl border border-input bg-background px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            placeholder="e.g. 142/300" data-testid="onboarding-mock"
          />
        </div>
      ),
    },
    {
      title: "Build my preparation profile",
      sub: "The AI will use everything above to create your initial strategy.",
      body: (
        <div className="rounded-2xl border border-border bg-card p-6 space-y-2.5 text-sm">
          {[["Exam", data.exam], ["Target", data.target], ["Exam date", data.exam_date], ["Level", data.level], ["Daily hours", data.daily_hours], ["Latest mock", data.mock_score || "—"]].map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-medium">{v || "—"}</span>
            </div>
          ))}
        </div>
      ),
    },
  ];

  if (done) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#040406] text-white text-center px-6">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center mb-8 glow-cyan">
          <Check className="h-8 w-8 text-white" />
        </motion.div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Your sarkarijobai dashboard is ready.</h1>
        <p className="mt-4 text-white/60 max-w-md">Welcome to sarkarijobai. Your preparation starts now.</p>
        <button onClick={() => navigate("/dashboard")}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-8 py-3.5 hover:scale-105 transition-transform"
          data-testid="onboarding-go-dashboard">
          Enter dashboard <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (building) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#040406] text-white text-center px-6">
        <div className="relative mb-8">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center glow-cyan animate-float">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-white/70"><Loader2 className="h-4 w-4 animate-spin" /> Building your initial preparation profile…</div>
      </div>
    );
  }

  const cur = steps[step];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
            <Plane className="h-4 w-4 text-white -rotate-45" />
          </div>
          <span className="font-display text-lg font-bold">sarkarijobai</span>
        </div>
        <div className="h-1 bg-muted">
          <motion.div className="h-full bg-gradient-to-r from-amber-400 to-red-600" animate={{ width: `${((step + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">Step {step + 1} of {total}</p>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{cur.title}</h1>
              <p className="mt-2 text-sm text-muted-foreground mb-8">{cur.sub}</p>
              {cur.body}
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-between">
            <button onClick={back} disabled={step === 0}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground disabled:opacity-0"
              data-testid="onboarding-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < total - 1 ? (
              <button onClick={next} disabled={!canProceed()}
                className="inline-flex items-center gap-2 rounded-full bg-foreground text-background font-medium px-7 py-3 hover:scale-105 transition-transform disabled:opacity-40"
                data-testid="onboarding-next">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={finish}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-7 py-3 hover:scale-105 transition-transform"
                data-testid="onboarding-finish">
                Build my profile <Sparkles className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
